const COMPLETED_STATUSES = new Set(["PAID", "COMPLETE"]);

const emptyBucket = () => ({ platform: 0, driver: 0, rides: 0 });

export function getPlatformUtilityFromBooking(booking) {
  const fees = parseFloat(booking?.convenience_fees || 0);
  const discount = parseFloat(booking?.discount || 0);
  return fees - discount;
}

export function getDriverShareFromBooking(booking) {
  return parseFloat(booking?.driver_share || 0);
}

export function isCompletedPartnerBooking(booking) {
  return COMPLETED_STATUSES.has(booking?.status);
}

function startOfWeek(ref) {
  const start = new Date(ref);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  const mondayOffset = day === 0 ? 6 : day - 1;
  start.setDate(start.getDate() - mondayOffset);
  return start;
}

function endOfWeek(ref) {
  const end = startOfWeek(ref);
  end.setDate(end.getDate() + 7);
  return end;
}

export function isBookingInWeek(tripdate, ref = new Date()) {
  if (!tripdate) return false;
  const d = new Date(tripdate);
  return d >= startOfWeek(ref) && d < endOfWeek(ref);
}

export function isBookingInMonth(tripdate, ref = new Date()) {
  if (!tripdate) return false;
  const d = new Date(tripdate);
  return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
}

export function isBookingInQuarter(tripdate, ref = new Date()) {
  if (!tripdate) return false;
  const d = new Date(tripdate);
  const quarter = Math.floor(ref.getMonth() / 3);
  return d.getFullYear() === ref.getFullYear() && Math.floor(d.getMonth() / 3) === quarter;
}

export function isBookingInYear(tripdate, ref = new Date()) {
  if (!tripdate) return false;
  const d = new Date(tripdate);
  return d.getFullYear() === ref.getFullYear();
}

function roundMoney(value, decimal = 2) {
  return parseFloat(parseFloat(value || 0).toFixed(decimal));
}

function accumulateBucket(bucket, platform, driver) {
  bucket.platform += platform;
  bucket.driver += driver;
  bucket.rides += 1;
}

export function computePartnerEarningsSummary(bookings, driverId, settings, ref = new Date()) {
  const decimal = settings?.decimal ?? 2;
  const summary = {
    week: emptyBucket(),
    month: emptyBucket(),
    quarter: emptyBucket(),
    year: emptyBucket(),
    lifetime: emptyBucket(),
  };

  (bookings || []).forEach((booking) => {
    if (!booking || booking.driver !== driverId || !isCompletedPartnerBooking(booking)) {
      return;
    }
    if (booking.driver_share === undefined && booking.convenience_fees === undefined) {
      return;
    }

    const platform = getPlatformUtilityFromBooking(booking);
    const driver = getDriverShareFromBooking(booking);
    const tripdate = booking.tripdate;

    accumulateBucket(summary.lifetime, platform, driver);
    if (isBookingInWeek(tripdate, ref)) accumulateBucket(summary.week, platform, driver);
    if (isBookingInMonth(tripdate, ref)) accumulateBucket(summary.month, platform, driver);
    if (isBookingInQuarter(tripdate, ref)) accumulateBucket(summary.quarter, platform, driver);
    if (isBookingInYear(tripdate, ref)) accumulateBucket(summary.year, platform, driver);
  });

  Object.keys(summary).forEach((key) => {
    summary[key].platform = roundMoney(summary[key].platform, decimal);
    summary[key].driver = roundMoney(summary[key].driver, decimal);
  });

  return summary;
}

export function buildDriverEarningsIndex(bookings, settings, ref = new Date()) {
  const driverIds = new Set();
  (bookings || []).forEach((booking) => {
    if (booking?.driver && isCompletedPartnerBooking(booking)) {
      driverIds.add(booking.driver);
    }
  });
  const index = {};
  driverIds.forEach((driverId) => {
    index[driverId] = computePartnerEarningsSummary(bookings, driverId, settings, ref);
  });
  return index;
}
